/* eslint-disable @atlaskit/design-system/use-primitives-text */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, jsx } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import { type IconProp } from '@atlaskit/button/new';
import VideoClipIcon from '@atlaskit/icon-lab/core/video-clip';
import CommentIcon from '@atlaskit/icon/core/comment';
import PageIcon from '@atlaskit/icon/core/page';
import PenIcon from '@atlaskit/icon/core/pen';
import PullRequestIcon from '@atlaskit/icon/core/pull-request';
import TaskIcon from '@atlaskit/icon/core/task';
import { token } from '@atlaskit/tokens';

import { SectionName } from '../../../../../common/analytics/section-name';
import { useSectionReveal } from '../../../../../common/analytics/useSectionReveal';
import { useAtlassianUserContext } from '../../../../../hooks/use-user-context';
import type { OrgMetricsResponse } from '../../../../../utils/orgMetrics';
import { SpacingWrapper } from '../../../SpacingWrapper';

import { messages } from './messages';

const styles = cssMap({
	section: {
		maxWidth: '100%',
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.1000'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingTop: '80px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingBottom: '180px',
	},
	title: {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#B7B9BE',
		font: token('font.heading.large'),
		fontFamily: token('font.family.brand.heading'),
		fontWeight: token('font.weight.regular'),
		// eslint-disable-next-line  @atlaskit/design-system/use-tokens-typography
		lineHeight: 1.5,
		margin: 0,
	},

	// headline stat
	headlineStat: {
		display: 'flex',
		flexDirection: 'column',
		// gap: token('space.500'),
	},
	headlineStatLabel: {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#B7B9BE',
		font: token('font.heading.large'),
		fontFamily: token('font.family.brand.heading'),
		fontWeight: token('font.weight.regular'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '150%',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		letterSpacing: '0.5px',
	},
	headlineStatValue: {
		font: token('font.body'),
		textAlign: 'justify',
		textJustify: 'distribute',
		fontFamily: token('font.family.body'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: '475px',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#B7B9BE',
		fontVariantNumeric: 'lining-nums proportional-nums',
		fontFeatureSettings: "'ss01' on, 'ss02' on, 'ss07' on, 'cv11' on",
		maxWidth: '100%',
		margin: 0,
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontWeight: 300,
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '100%',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		letterSpacing: '3px',
		'@media (max-width: 30rem)': {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			fontSize: '120px',
		},
	},
	statGrid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(6, 1fr)',
		gap: token('space.300'),
		'@media (max-width: 30rem)': {
			gridTemplateColumns: 'repeat(2, 1fr)',
		},
	},
	statCard: {
		borderRadius: token('radius.xlarge'),
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.150'),
	},
	statNumber: {
		fontFamily: token('font.family.body'),
		fontWeight: token('font.weight.regular'),
		margin: 0,
		fontVariantNumeric: 'lining-nums tabular-nums',
		fontFeatureSettings: "'ss01' on, 'ss02' on, 'ss07' on, 'cv11' on",
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: '1.5rem',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '1.33',
	},
	statLabelContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#7D818A',
	},
	statLabel: {
		font: token('font.body'),
		margin: 0,
	},
});

type StatCardProps = {
	count: number;
	label: string;
	icon: IconProp;
};

function StatCard({ count, label, icon: Icon }: StatCardProps): JSX.Element {
	const { formatNumber } = useIntl();
	return (
		<div css={styles.statCard}>
			<div css={styles.statLabelContainer}>
				<Icon label={label} />
				{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
				<p css={styles.statLabel}>{label}</p>
			</div>
			{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
			<p css={styles.statNumber}>{formatNumber(count)}</p>
		</div>
	);
}

export function IntroductionSection({ metrics }: { metrics: OrgMetricsResponse }): JSX.Element {
	const { formatMessage, formatNumber } = useIntl();
	const { totalNodes } = metrics;
	const { identity } = useAtlassianUserContext();
	const sectionRef = useSectionReveal<HTMLElement>(SectionName.Introduction);

	const nodeCount = totalNodes ?? 0;
	return (
		<SpacingWrapper>
			<section ref={sectionRef} css={styles.section}>
				{/* eslint-disable-next-line @atlaskit/design-system/use-heading */}
				<h2 css={styles.title}>
					<FormattedMessage
						{...messages.title}
						values={{ br: <br />, username: identity?.name?.split(' ')[0] }}
					/>
				</h2>

				<div css={styles.headlineStat}>
					{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text, @atlassian/i18n/no-literal-string-in-jsx */}
					<p css={styles.headlineStatLabel}>
						<FormattedMessage {...messages.headlineStatLabel} />
					</p>

					{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
					<p css={styles.headlineStatValue}>
						{formatNumber(
							nodeCount,
							nodeCount > 9999
								? {
										notation: 'compact',
										maximumFractionDigits: 2,
									}
								: undefined,
						)}
					</p>
				</div>

				{/* 6 stat cards */}
				<div css={styles.statGrid}>
					<StatCard
						count={metrics.pagesCreated30d ?? 0}
						label={formatMessage(messages.pagesLabel)}
						icon={PageIcon}
					/>
					<StatCard
						count={metrics.totalJiraIssues ?? 0}
						label={formatMessage(messages.workItemsLabel)}
						icon={TaskIcon}
					/>
					<StatCard
						count={metrics.loomsRecorded30d ?? 0}
						label={formatMessage(messages.videosLabel)}
						icon={VideoClipIcon}
					/>
					<StatCard count={1234} label={formatMessage(messages.designsLabel)} icon={PenIcon} />
					<StatCard
						count={1234}
						label={formatMessage(messages.pullRequestsLabel)}
						icon={PullRequestIcon}
					/>
					<StatCard
						count={metrics.commentsCreated7d ?? 0}
						label={formatMessage(messages.commentsLabel)}
						icon={CommentIcon}
					/>
				</div>
			</section>
		</SpacingWrapper>
	);
}
